import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { compose } from '@nobia/zeus-components/lib/recompose'
import { gql, graphql } from '@nobia/zeus-components/lib/apollo'
import styled, { prop } from '@nobia/zeus-components/lib/styled'
import Button from '@nobia/zeus-components/lib/buttons'
import { Container, Row, Column } from '@nobia/zeus-components/lib/grid'
import { CheckBox, InputField } from '@nobia/zeus-components/lib/forms'
import { trackEvent, OutboundLink } from '@nobia/zeus-components/lib/analytics'
import { SubHeadline, Paragraph } from '@nobia/zeus-components/lib/text'
import ConfirmationModal from '../confirmation-modal/ConfirmationModal'
import { cdnImage } from '../../utils'

const image = cdnImage(require('../../assets/close.svg'))

const Modal = styled.div`
  position: fixed;
  display: block;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1050;
  overflow: hidden;
  -webkit-overflow-scrolling: touch;
  outline: 0;
`

const ModalDialog = styled.div`
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  margin: 0;
`

const ModalContent = styled.div`
  position: relative;
  background-color: #fff;
  -webkit-background-clip: padding-box;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.2);
  outline: 0;
  -webkit-box-shadow: 0 3px 9px rgba(0, 0, 0, 0.5);
  box-shadow: 0 3px 9px rgba(0, 0, 0, 0.5);
  min-height: 100vh;
  border-radius: 0;
`

const ModalHeader = styled.div`
  display: block;
  padding: 15px;
  text-align: right;
`

const CloseButton = styled.img`
  width: 7%;
  vertical-align: middle;
  border: 0;
`

const ModalBody = styled.div`
  position: relative;
  display: block;
  padding: 15px;
`

const ShareWishlistButton = styled(Button)`
  margin: 0;
`

const CustomLabel = styled.label`
  width: 100%;
  line-height: 10px;
`

const CheckboxColumn = styled(Column)`
  padding: 0 0 0 20px;
  text-align: left;
  margin-bottom: 10px;
`

const LabelColumn = styled(Column)`
  padding: 0 20px 0 20px;
  text-align: left;
  font-size: 14px;
  margin-bottom: 10px;
`

const HeaderColumn = styled(Column)`
  padding: 0 20px 0 20px;
`

const InputColumn = styled(Column)`
  padding: 10px 20px 0 20px;
`

const StyledInputField = styled(InputField)`
  font-size: 16px;
`

const PrivacyNotice = styled(Paragraph)`
  margin-top: 20px;
  color: ${prop('theme.colors.darkGray')};
`

const wishlistMutation = gql`
  mutation wishlist($input: WishlistInput) {
    createWishlist(input: { wishlist: $input }) {
      statusCode
    }
  }
`

const trackAnalyticsEvent = () => {
  trackEvent(
    'MC-ESH Share Wishlist',
    'MC-ESH Click',
    'Share Wishlist Email Submitted'
  )
}

const trackAnalyticsEventCheckbox = () => {
  trackEvent(
    'MC-ESH Share Wishlist',
    'MC-ESH Click',
    'Share Wishlist Contact me back'
  )
}

class ShareModal extends Component {
  state = {
    emailInputValid: null,
    emailInputError: null,
    emailInputDefaultValue: null,
    contactCheckboxChecked: false,
    showConfirmationModal: false,
  }

  generateInput = () => {
    const input = {
      customerEmail: this.emailInput.value,
      storeEmail: 'sutton.shop@magnet.co.uk',
      storeName: 'Sutton',
      storeManagerEmail: 'sutton.sm@magnet.co.uk',
      triggeredBy: 'USER',
      contactMe: this.state.contactCheckboxChecked,
      products: this.getTransformedProducts(),
    }

    return input
  }

  getTransformedProducts = () => {
    const transformed = this.props.wishlist.map(x => {
      const mapped = {
        id: x.id,
        title: x.name,
        price: x.price,
        image: x.images && x.images.length >= 1 ? x.images[0] : null,
        productPageUrl: null,
      }
      return mapped
    })
    return transformed
  }

  handleSubmit = () => {
    if (this.validateEmail()) {
      // do the customerio calls
      const input = this.generateInput()
      this.props.customerIoMutation({ variables: { input } })

      this.toggleConfirmationModal()
    }
  }

  toggleConfirmationModal = () => {
    this.setState({ showConfirmationModal: !this.state.showConfirmationModal })
  }

  clickHandler = () => {
    this.handleSubmit()
    trackAnalyticsEvent()
  }

  validateEmail = () => {
    const re = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i // eslint-disable-line
    const result = re.test(this.emailInput.value)
    if (!result) {
      this.setState({
        emailInputValid: false,
        emailInputError: 'Please enter a valid email address',
      })
    } else {
      this.setState({ emailInputValid: null, emailInputError: null })
    }
    return result
  }

  toggleContactCheckboxState = () => {
    this.setState({
      contactCheckboxChecked: !this.state.contactCheckboxChecked,
    })
  }

  clickHandlerContact = () => {
    this.toggleContactCheckboxState()
    trackAnalyticsEventCheckbox()
  }

  handleClose = () => {
    this.setState(
      {
        emailInputValid: null,
        emailInputError: null,
        emailInputDefaultValue: null,
        contactCheckboxChecked: false,
      },
      this.props.handleClose
    )
  }

  render() {
    if (this.props.open) {
      return (
        <Modal>
          <ModalDialog>
            <ModalContent>
              <ModalHeader>
                <CloseButton src={image} onClick={this.handleClose} />
              </ModalHeader>
              <ModalBody>
                <Container>
                  <Row>
                    <HeaderColumn xs={12}>
                      <SubHeadline>Share my saved products</SubHeadline>
                    </HeaderColumn>
                  </Row>
                  <Row>
                    <Column xs={12}>
                      <CustomLabel>
                        Send all saved products to you or someone else via email
                      </CustomLabel>
                    </Column>
                  </Row>
                  <Row>
                    <InputColumn xs={12}>
                      <StyledInputField
                        type={'text'}
                        placeholder={'E-mail'}
                        inputRef={el => {
                          this.emailInput = el
                        }}
                        valid={this.state.emailInputValid}
                        error={this.state.emailInputError}
                        defaultValue={this.state.emailInputDefaultValue}
                      />
                    </InputColumn>
                  </Row>
                  <Row>
                    <CheckboxColumn xs={1}>
                      <CheckBox
                        checkboxContactRef={el => {
                          this.contactCheckbox = el
                        }}
                        onChange={this.clickHandlerContact}
                      />
                    </CheckboxColumn>
                    <LabelColumn xs={11}>
                      By ticking this box, I confirm that I am happy for Magnet
                      to contact me via email about Magnet’s kitchen
                      inspiration, latest product offers, competitions and
                      services.
                    </LabelColumn>
                  </Row>
                  <Row>
                    <Column xs={12}>
                      <ShareWishlistButton
                        kind={'primary'}
                        size={'large'}
                        block
                        onClick={this.clickHandler}
                      >
                        Share wishlist
                      </ShareWishlistButton>
                    </Column>
                  </Row>
                  <Row>
                    <Column xs={12}>
                      <PrivacyNotice>
                        At Magnet, we promise to keep your data safe and
                        private. To find out more, please read our{' '}
                        <OutboundLink href="https://www.magnet.co.uk/privacy-notice/">
                          Privacy notice.
                        </OutboundLink>
                      </PrivacyNotice>
                    </Column>
                  </Row>
                  <ConfirmationModal
                    open={this.state.showConfirmationModal}
                    handleClose={this.toggleConfirmationModal}
                  />
                </Container>
              </ModalBody>
            </ModalContent>
          </ModalDialog>
        </Modal>
      )
    }
    return <div />
  }
}

ShareModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  customerIoMutation: PropTypes.func.isRequired,
  wishlist: PropTypes.array.isRequired, // eslint-disable-line
}

const enhanced = compose(
  graphql(wishlistMutation, { name: 'customerIoMutation' })
)

export default enhanced(ShareModal)
